import { useState, useMemo } from 'react'
import {
  Container,
  Typography,
  Box,
  Stack,
  Card,
  CardContent,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Avatar,
  Button,
} from '@mui/material'
import { Download, FilterList, CheckCircle } from '@mui/icons-material'
import { format, parseISO } from 'date-fns'
import { useTaskStore } from '@/store/useTaskStore'
import { useFamilyStore } from '@/store/useFamilyStore'

export default function ArchivePage() {
  const [searchQuery, setSearchQuery] = useState('')
  const [filterMember, setFilterMember] = useState<string>('All')
  const [sortBy, setSortBy] = useState<'date' | 'time'>('date')

  const completions = useTaskStore((state) => state.completions)
  const tasks = useTaskStore((state) => state.tasks)
  const members = useFamilyStore((state) => state.members)

  // Filter completions
  const filteredCompletions = useMemo(() => {
    let filtered = completions

    if (filterMember !== 'All') {
      filtered = filtered.filter((c) => c.completed_by === filterMember)
    }

    if (searchQuery) {
      filtered = filtered.filter((c) => {
        const task = tasks.find((t) => t.id === c.task_id)
        return task?.title.toLowerCase().includes(searchQuery.toLowerCase())
      })
    }

    // Sort
    return filtered.sort((a, b) => {
      if (sortBy === 'date') {
        return new Date(b.completed_at).getTime() - new Date(a.completed_at).getTime()
      } else {
        return (b.time_taken || 0) - (a.time_taken || 0)
      }
    })
  }, [completions, tasks, filterMember, searchQuery, sortBy])

  const handleExport = () => {
    // Create CSV content
    const headers = [
      'Date',
      'Task',
      'Completed By',
      'Time Taken',
      'On Time',
      'Notes',
    ]

    const rows = filteredCompletions.map((completion) => {
      const task = tasks.find((t) => t.id === completion.task_id)
      const member = members.find((m) => m.id === completion.completed_by)

      return [
        format(parseISO(completion.completed_at), 'yyyy-MM-dd HH:mm'),
        task?.title || 'Unknown',
        member?.name || 'Unknown',
        completion.time_taken ? `${completion.time_taken} min` : 'N/A',
        completion.on_time ? 'Yes' : 'No',
        completion.notes || '',
      ]
    })

    const csvContent = [
      headers.join(','),
      ...rows.map((row) => row.map((cell) => `"${cell}"`).join(',')),
    ].join('\n')

    // Download CSV
    const blob = new Blob([csvContent], { type: 'text/csv' })
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `task-completions-${format(new Date(), 'yyyy-MM-dd')}.csv`
    a.click()
    window.URL.revokeObjectURL(url)
  }

  // Calculate stats
  const stats = useMemo(() => {
    const totalCompleted = filteredCompletions.length
    const onTimeCount = filteredCompletions.filter((c) => c.on_time).length
    const avgTime =
      filteredCompletions.reduce((sum, c) => sum + (c.time_taken || 0), 0) /
      totalCompleted || 0

    return { totalCompleted, onTimeCount, avgTime }
  }, [filteredCompletions])

  return (
    <Container maxWidth="lg">
      <Box mb={4}>
        <Typography variant="h4" gutterBottom>
          Archive
        </Typography>
        <Typography variant="body1" color="text.secondary">
          View completed task history and export data
        </Typography>
      </Box>

      {/* Stats Cards */}
      <Stack direction="row" spacing={2} mb={4} flexWrap="wrap">
        <Card sx={{ flex: 1, minWidth: 200 }}>
          <CardContent>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Total Completed
            </Typography>
            <Typography variant="h4">{stats.totalCompleted}</Typography>
          </CardContent>
        </Card>

        <Card sx={{ flex: 1, minWidth: 200 }}>
          <CardContent>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              On-Time Rate
            </Typography>
            <Typography variant="h4">
              {stats.totalCompleted > 0
                ? Math.round((stats.onTimeCount / stats.totalCompleted) * 100)
                : 0}
              %
            </Typography>
          </CardContent>
        </Card>

        <Card sx={{ flex: 1, minWidth: 200 }}>
          <CardContent>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Avg. Time
            </Typography>
            <Typography variant="h4">
              {Math.round(stats.avgTime)} min
            </Typography>
          </CardContent>
        </Card>
      </Stack>

      {/* Filters */}
      <Box
        display="flex"
        gap={2}
        flexWrap="wrap"
        alignItems="center"
        mb={3}
        sx={{ bgcolor: 'background.paper', p: 2, borderRadius: 2 }}
      >
        <FilterList color="action" />

        <TextField
          label="Search Tasks"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          size="small"
          sx={{ minWidth: 200 }}
        />

        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Completed By</InputLabel>
          <Select
            value={filterMember}
            label="Completed By"
            onChange={(e) => setFilterMember(e.target.value)}
          >
            <MenuItem value="All">All Members</MenuItem>
            {members.map((member) => (
              <MenuItem key={member.id} value={member.id}>
                {member.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Sort By</InputLabel>
          <Select
            value={sortBy}
            label="Sort By"
            onChange={(e) => setSortBy(e.target.value as 'date' | 'time')}
          >
            <MenuItem value="date">Date (Newest)</MenuItem>
            <MenuItem value="time">Time Taken</MenuItem>
          </Select>
        </FormControl>

        <Button
          variant="outlined"
          startIcon={<Download />}
          onClick={handleExport}
          disabled={filteredCompletions.length === 0}
        >
          Export CSV
        </Button>
      </Box>

      {/* Results */}
      <Typography variant="body2" color="text.secondary" mb={2}>
        Showing {filteredCompletions.length} completions
      </Typography>

      {filteredCompletions.length === 0 ? (
        <Card>
          <CardContent>
            <Typography variant="body1" color="text.secondary" textAlign="center" py={4}>
              No completed tasks found. Start completing tasks to see them here!
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <Stack spacing={2}>
          {filteredCompletions.map((completion) => {
            const task = tasks.find((t) => t.id === completion.task_id)
            const member = members.find((m) => m.id === completion.completed_by)

            return (
              <Card key={completion.id}>
                <CardContent>
                  <Box display="flex" alignItems="flex-start" gap={2}>
                    {member && (
                      <Avatar
                        src={member.avatar_url || undefined}
                        sx={{
                          width: 40,
                          height: 40,
                          bgcolor: member.color,
                        }}
                      >
                        {member.name[0]}
                      </Avatar>
                    )}

                    <Box flex={1}>
                      <Box display="flex" alignItems="center" gap={1} mb={1}>
                        <CheckCircle color="success" fontSize="small" />
                        <Typography variant="h6">
                          {task?.title || 'Unknown Task'}
                        </Typography>
                      </Box>

                      <Box display="flex" gap={1} flexWrap="wrap" mb={1}>
                        <Chip
                          label={member?.name || 'Unknown'}
                          size="small"
                          variant="outlined"
                        />
                        <Chip
                          label={format(
                            parseISO(completion.completed_at),
                            'PPp'
                          )}
                          size="small"
                          variant="outlined"
                        />
                        {completion.time_taken && (
                          <Chip
                            label={`${completion.time_taken} min`}
                            size="small"
                            color="primary"
                          />
                        )}
                        <Chip
                          label={completion.on_time ? 'On Time' : 'Late'}
                          size="small"
                          color={completion.on_time ? 'success' : 'warning'}
                        />
                      </Box>

                      {completion.notes && (
                        <Typography variant="body2" color="text.secondary">
                          Notes: {completion.notes}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            )
          })}
        </Stack>
      )}
    </Container>
  )
}
